import { useState, useEffect, useCallback, useMemo } from "react"
import { useTheme } from "@material-ui/core/styles"
import { useTranslation } from "react-i18next"
import PasswordValidator from "password-validator"

import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"

const schema = new PasswordValidator()
schema
    .is()
    .min(8)
    .has()
    .uppercase()
    .has()
    .lowercase()
    .has()
    .digits()
    .has()
    .symbols()

export const usePasswordChecklist = password => {
    const { t } = useTranslation()

    // Lowercase are used to match the schema rule names.
    const validator = useMemo(
        () => ({
            min: t("general.components.userModal.atLeastEightCharacters"),
            uppercase: t("general.components.userModal.atLeastOneUppercaseCharacter"),
            lowercase: t("general.components.userModal.atLeastOneLowercaseCharacter"),
            digits: t("general.components.userModal.atLeastOneNumericCharacter"),
            symbols: t("general.components.userModal.atLeastOneSymbol")
        }),
        [t]
    )

    const [errors, setErrors] = useState({})

    useEffect(() => {
        const names = schema.validate(password, { list: true })
        if (Array.isArray(names)) {
            setErrors(Object.fromEntries(names.map(name => [name, true])))
        }
    }, [password])

    const ValidationList = useCallback(
        () => (
            <ul>
                {Object.entries(validator).map(([name, message]) => (
                    <li key={name}>
                        <ValidationItem isValid={!Boolean(errors[name])}>
                            {message}
                        </ValidationItem>
                    </li>
                ))}
            </ul>
        ),
        [errors, validator]
    )

    function render() {
        return <ValidationList />
    }

    return [Boolean(Object.keys(errors).length), render]
}

const ValidationItem = props => {
    const { children, isValid } = props
    const { palette } = useTheme()
    const color = isValid ? palette.success.dark : palette.grey[600]

    return (
        <Box display="flex" alignItems="center" gridColumnGap={5}>
            <CheckCircleIcon
                htmlColor={color}
                style={{ fontSize: 16, visibility: isValid ? "visible" : "hidden" }}
            />
            <Typography variant="body2" style={{ color }}>
                {children}
            </Typography>
        </Box>
    )
}
